import React, {PureComponent} from 'react';
import {List, CellMeasurer, CellMeasurerCache, AutoSizer, WindowScroller} from 'react-virtualized';
import debounce from 'debounce';

class VirtualizedList extends PureComponent {
  cache = new CellMeasurerCache({
    fixedWidth: true,
    defaultHeight: this.props.defaultHeight || 100
  });

  state = {
    items: []
  };

  componentDidMount() {
    const {items} = this.props;

    this.setState({items});
    window.addEventListener('resize', this.handleWindowResize);
  }

  componentWillUnmount() {
    window.removeEventListener('resize', this.handleWindowResize);
  }

  componentDidUpdate(prevProps) {
    const {items, overrideListReset = false} = this.props;

    //Sometimes this doesn't have the desired outcome, so let the implementer have the option to over ride.
    if (prevProps.items !== items && !overrideListReset) {
      // Clear the measurement cache when items change to ensure correct heights for new items
      this.cache.clearAll();

      // This exists because of a quirk with react-virtualized. When the length of the list does not change,
      // react-virtualized will not re-render the list. As a work-around, this method first sets the items array cache
      // to empty. When the state is updated, we reset the items array cache.
      // If you're finding that this causes unnecessary re-rendering, memoize the items prop.
      this.setState({items: []}, () => this.setState({items}));
    }
  }

  setListRef = node => {
    this.list = node;
  }

  handleWindowResize = debounce(() => {
    // When the window is resized, the measurement cache is invalid as elements have a fluid width with respect to the
    // browser viewport size. So we need to force a re-calculation so elements to not lose their position.
    if (this.list) {
      this.cache.clearAll();
      this.list.forceUpdateGrid();
    }
  }, 250);

  renderRow = ({index, key, parent, style}) => {
    const {items} = this.state;
    const {measurable, children} = this.props;
    const item = items[index];

    return (
      <CellMeasurer
        cache={this.cache}
        columnIndex={0}
        key={key}
        parent={parent}
        rowIndex={index}>
        {measurable ? (({measure}) => (
          <div style={style}>
            {children(item, index, measure)}
          </div>
        )) : (
          <div style={style}>
            {children(item, index)}
          </div>
        )}
      </CellMeasurer>
    );
  };

  render() {
    const {window, target, style} = this.props;
    const {items} = this.state;

    if (window) {
      return (
        <WindowScroller scrollElement={target}>
          {({height, isScrolling, registerChild, onChildScroll, scrollTop}) => (
            <AutoSizer disableHeight>
              {({width}) => (
                <div ref={registerChild}>
                  <List
                    style={style}
                    ref={this.setListRef}
                    autoHeight
                    height={height}
                    isScrolling={isScrolling}
                    onScroll={onChildScroll}
                    overscanRowCount={5}
                    rowCount={items.length}
                    rowHeight={this.cache.rowHeight}
                    deferredMeasurementCache={this.cache}
                    rowRenderer={this.renderRow}
                    scrollToIndex={-1}
                    scrollTop={scrollTop}
                    width={width}
                  />
                </div>
              )}
            </AutoSizer>
          )}
        </WindowScroller>
      );
    }

    return (
      <AutoSizer>
        {({width, height}) => (
          <List
            style={style}
            ref={this.setListRef}
            height={height}
            overscanRowCount={5}
            rowCount={items.length}
            rowHeight={this.cache.rowHeight}
            deferredMeasurementCache={this.cache}
            rowRenderer={this.renderRow}
            width={width}
          />
        )}
      </AutoSizer>
    )
  }
}

export default VirtualizedList;